import { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { DateRange } from '@mui/icons-material';

interface FeedbackDateRangePickerProps {
  startDate: string | null;
  endDate: string | null;
  onChange: (startDate: string | null, endDate: string | null) => void;
  disabled?: boolean;
  defaultPreset?: '7days' | '30days' | '90days' | 'custom';
}

type PresetOption = '7days' | '30days' | '90days' | 'custom';

export function FeedbackDateRangePicker({
  startDate,
  endDate,
  onChange,
  disabled = false,
  defaultPreset = '7days',
}: FeedbackDateRangePickerProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [preset, setPreset] = useState<PresetOption>(defaultPreset);
  const [customStart, setCustomStart] = useState<string>('');
  const [customEnd, setCustomEnd] = useState<string>('');

  // Apply default preset on mount
  useEffect(() => {
    if (!startDate && !endDate && defaultPreset !== 'custom') {
      applyPreset(defaultPreset);
    }
  }, []);

  const applyPreset = useCallback(
    (newPreset: PresetOption) => {
      if (newPreset === 'custom') {
        return;
      }

      const now = new Date();
      const end = now.toISOString().split('T')[0];
      let start: string;

      switch (newPreset) {
        case '7days':
          start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
            .toISOString()
            .split('T')[0];
          break;
        case '30days':
          start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
            .toISOString()
            .split('T')[0];
          break;
        case '90days':
          start = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000)
            .toISOString()
            .split('T')[0];
          break;
        default:
          start = end;
      }

      onChange(start, end);
    },
    [onChange],
  );

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handlePresetChange = useCallback(
    (newPreset: PresetOption) => {
      setPreset(newPreset);

      if (newPreset === 'custom') {
        return;
      }

      applyPreset(newPreset);
      handleClose();
    },
    [applyPreset],
  );

  const handleCustomApply = useCallback(() => {
    if (customStart && customEnd) {
      onChange(customStart, customEnd);
      handleClose();
    }
  }, [customStart, customEnd, onChange]);

  const getDisplayText = () => {
    if (startDate && endDate) {
      return `${startDate} to ${endDate}`;
    }
    return 'Select date range';
  };

  const open = Boolean(anchorEl);
  const id = open ? 'date-range-popover' : undefined;

  return (
    <>
      <Button
        aria-describedby={id}
        onClick={handleClick}
        variant="outlined"
        disabled={disabled}
        startIcon={<DateRange />}
        sx={{ minWidth: 200 }}
      >
        {getDisplayText()}
      </Button>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, minWidth: 300 }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Select Date Range
          </Typography>

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Preset</InputLabel>
            <Select
              value={preset}
              label="Preset"
              onChange={(e) => handlePresetChange(e.target.value as PresetOption)}
            >
              <MenuItem value="7days">Last 7 days</MenuItem>
              <MenuItem value="30days">Last 30 days</MenuItem>
              <MenuItem value="90days">Last 90 days</MenuItem>
              <MenuItem value="custom">Custom range</MenuItem>
            </Select>
          </FormControl>

          {preset === 'custom' && (
            <>
              <TextField
                fullWidth
                size="small"
                label="Start Date"
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                size="small"
                label="End Date"
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
              />
              <Button
                fullWidth
                variant="contained"
                onClick={handleCustomApply}
                disabled={!customStart || !customEnd}
              >
                Apply
              </Button>
            </>
          )}
        </Box>
      </Popover>
    </>
  );
}
